import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BusinessDashboardNav from '../../components/ui/BusinessDashboardNav';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import VenueForm from './components/VenueForm';
import VenueList from './components/VenueList';
import DuplicateVenueModal from './components/DuplicateVenueModal';
import { getAllVenues, createVenue, updateVenue, deleteVenue, checkDuplicateVenue } from '../../services/venueService';

const VenueManagementPage = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [duplicateVenues, setDuplicateVenues] = useState([]);
  const [pendingVenueData, setPendingVenueData] = useState(null);
  
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Check if user has access to venue management (admin or manager)
  useEffect(() => {
    if (userProfile && !['admin', 'manager']?.includes(userProfile?.role)) {
      navigate('/');
      return;
    }
  }, [userProfile, navigate]);

  const loadVenues = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (selectedCity) filters.city = selectedCity;
      
      const data = await getAllVenues(filters);
      setVenues(data || []);
    } catch (error) {
      setError(`Failed to load venues: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVenues();
  }, [searchTerm, selectedCity]);

  const handleCreateVenue = async (venueData) => {
    try {
      setError('');
      setSuccess('');

      // Check for duplicates
      const duplicates = await checkDuplicateVenue(
        venueData?.name,
        venueData?.address_line_1,
        venueData?.city
      );

      if (duplicates?.length > 0) {
        setDuplicateVenues(duplicates);
        setPendingVenueData(venueData);
        return;
      }

      const newVenue = await createVenue(venueData);
      setSuccess('Venue created successfully');
      setIsFormVisible(false);
      setSelectedVenue(null);
      loadVenues();
    } catch (error) {
      setError(`Failed to create venue: ${error?.message}`);
    }
  };

  const handleUpdateVenue = async (venueData) => {
    try {
      setError('');
      setSuccess('');

      if (!selectedVenue?.id) {
        setError('No venue selected for update');
        return;
      }

      const updatedVenue = await updateVenue(selectedVenue?.id, venueData);
      setSuccess('Venue updated successfully');
      setIsFormVisible(false);
      setSelectedVenue(null);
      loadVenues();
    } catch (error) {
      setError(`Failed to update venue: ${error?.message}`);
    }
  };

  const handleDeleteVenue = async (venueId) => {
    if (!window.confirm('Are you sure you want to delete this venue? This action cannot be undone.')) {
      return;
    }

    try {
      setError('');
      await deleteVenue(venueId);
      setSuccess('Venue deleted successfully');
      loadVenues();
    } catch (error) {
      setError(`Failed to delete venue: ${error?.message}`);
    }
  };

  const handleEditVenue = (venue) => {
    setSelectedVenue(venue);
    setIsFormVisible(true);
  };

  const handleConfirmDuplicate = async () => {
    if (pendingVenueData) {
      try {
        const newVenue = await createVenue(pendingVenueData);
        setSuccess('Venue created successfully');
        setIsFormVisible(false);
        setSelectedVenue(null);
        setDuplicateVenues([]);
        setPendingVenueData(null);
        loadVenues();
      } catch (error) {
        setError(`Failed to create venue: ${error?.message}`);
      }
    }
  };

  const cityOptions = [
    { value: '', label: 'All Cities' },
    { value: 'Mumbai', label: 'Mumbai' },
    { value: 'Delhi', label: 'Delhi' },
    { value: 'Bangalore', label: 'Bangalore' },
    { value: 'Chennai', label: 'Chennai' },
    { value: 'Kolkata', label: 'Kolkata' }
  ];

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  if (!['admin', 'manager']?.includes(userProfile?.role)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <BusinessDashboardNav
        userRole={userProfile?.role || 'manager'}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        user={{
          name: userProfile?.full_name || "Event Manager",
          role: userProfile?.role === 'admin' ? "Platform Administrator" : "Event Manager"
        }}
      />
      <div className={`transition-all duration-300 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <header className="bg-card border-b border-border shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Venue Management</h1>
              <p className="text-sm text-muted-foreground">
                Create, edit, and manage venue information with comprehensive location details
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                onClick={() => {
                  setSelectedVenue(null);
                  setIsFormVisible(true);
                }}
              >
                Add Venue
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="LogOut"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </header>

        <main className="flex h-[calc(100vh-140px)]">
          {/* Left Section - Venue List */}
          <div className="w-1/2 border-r border-border flex flex-col">
            <div className="p-6 border-b border-border">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                  {success}
                </div>
              )}

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Search venues..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  iconName="Search"
                />
                <Select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e?.target?.value)}
                  options={cityOptions}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <VenueList
                venues={venues}
                loading={loading}
                onEdit={handleEditVenue}
                onDelete={handleDeleteVenue}
              />
            </div>
          </div>

          {/* Right Section - Venue Form */}
          <div className="w-1/2 flex flex-col">
            {isFormVisible ? (
              <VenueForm
                venue={selectedVenue}
                onSubmit={selectedVenue ? handleUpdateVenue : handleCreateVenue}
                onCancel={() => {
                  setIsFormVisible(false);
                  setSelectedVenue(null);
                }}
                isEditing={!!selectedVenue}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-muted-foreground">🏢</span>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Select or Create a Venue
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose a venue from the list to edit, or click "Add Venue" to create a new one
                  </p>
                  <Button
                    variant="default"
                    onClick={() => {
                      setSelectedVenue(null);
                      setIsFormVisible(true);
                    }}
                  >
                    Create New Venue
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* Duplicate Venue Modal */}
        {duplicateVenues?.length > 0 && (
          <DuplicateVenueModal
            duplicateVenues={duplicateVenues}
            onConfirm={handleConfirmDuplicate}
            onCancel={() => {
              setDuplicateVenues([]);
              setPendingVenueData(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default VenueManagementPage;